import React, { useCallback, useEffect, useState } from 'react'
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap'

import showToastNotification from '../../../../widgets/toastify'
import * as Constants from '../../../../../helpers/constants'
import {
  DEDUP_CACHE_PROPERTY_NAME,
  DEDUP_CACHE_SERVICES
} from '../helper/constants'
import {
  getDedupCacheDefaultState,
  normalizeDedupCacheResponse,
  buildDedupCacheValues,
  hasValidTtls,
  formatTtl
} from '../helper/helpers'

const DeduplicationCache = ({ onCheck, onUpdate }) => {
  const [state, setState] = useState(() => ({
    ...getDedupCacheDefaultState(),
    desc: '',
    isLoading: false,
    isSubmitting: false
  }))

  const fetchDedupCache = useCallback(() => {
    setState((prev) => ({ ...prev, isLoading: true }))

    onCheck(DEDUP_CACHE_PROPERTY_NAME)
      .then((response) => {
        if (response && response.status === Constants.CO_API.SUCCESS) {
          setState((prev) => ({
            ...prev,
            ...normalizeDedupCacheResponse(response.data && response.data.value)
          }))
        } else {
          showToastNotification({
            message: 'Failed to fetch deduplication cache settings',
            isSuccess: false
          })
        }
      })
      .catch((error) => {
        console.log(error)
        showToastNotification({
          message: error.message || 'Failed to fetch deduplication cache settings',
          isSuccess: false
        })
      })
      .finally(() => {
        setState((prev) => ({ ...prev, isLoading: false }))
      })
  }, [onCheck])

  useEffect(() => {
    fetchDedupCache()
  }, [fetchDedupCache])

  const onGlobalEnabledToggle = () => {
    setState((prev) => ({ ...prev, enabled: !prev.enabled }))
  }

  const onGlobalTtlChange = (e) => {
    const { value } = e.target
    setState((prev) => ({ ...prev, ttl: value }))
  }

  const onServiceEnabledToggle = (key) => {
    setState((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [key]: { ...prev.services[key], enabled: !prev.services[key].enabled }
      }
    }))
  }

  const onServiceTtlChange = (key, e) => {
    const { value } = e.target
    setState((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [key]: { ...prev.services[key], ttl: value }
      }
    }))
  }

  const onDescChange = (e) => {
    const { value } = e.target
    setState((prev) => ({ ...prev, desc: value }))
  }

  const handleSubmit = () => {
    if (!state.desc.trim()) {
      showToastNotification({
        message: 'Description is required',
        isSuccess: false
      })
      return
    }

    if (!hasValidTtls(state)) {
      showToastNotification({
        message: 'TTL values must be non-negative whole numbers',
        isSuccess: false
      })
      return
    }

    const requestBody = {
      name: DEDUP_CACHE_PROPERTY_NAME,
      value: buildDedupCacheValues(state),
      desc: state.desc
    }

    setState((prev) => ({ ...prev, isSubmitting: true }))

    onUpdate(requestBody)
      .then((response) => {
        if (response && response.status === Constants.CO_API.SUCCESS) {
          showToastNotification({
            message: 'Deduplication cache settings updated successfully',
            isSuccess: true
          })
        } else {
          showToastNotification({
            message: 'Failed to update deduplication cache settings',
            isSuccess: false
          })
        }
      })
      .catch((error) => {
        console.log(error)
        showToastNotification({
          message: error.message || 'Failed to update deduplication cache settings',
          isSuccess: false
        })
      })
      .finally(() => {
        setState((prev) => ({ ...prev, isSubmitting: false }))
      })
  }

  const enabledCount = DEDUP_CACHE_SERVICES.filter(({ key }) => state.services[key].enabled).length
  const isBusy = state.isLoading || state.isSubmitting

  return (
    <div className='dedup-cache'>
      <div className='dedup-cache__header'>
        <h5 className='dedup-cache__title'>
          Deduplication Cache
          {state.isLoading && (
            <Spinner
              animation='border'
              size='sm'
              className='dedup-cache__spinner'
            />
          )}
        </h5>
        <p className='dedup-cache__subtitle'>
          Enable caching per service and set how long (TTL, in seconds) deduplication results are kept.
        </p>
      </div>
      <Form>
        <div className={`dedup-card dedup-card--global ${state.enabled ? '' : 'dedup-card--off'}`}>
          <div className='dedup-card__head'>
            <div className='dedup-card__labels'>
              <span className='dedup-card__name'>Global</span>
              <span className='dedup-card__hint'>Master deduplication cache switch</span>
            </div>
            <Form.Check
              type='switch'
              id='dedup-global-switch'
              checked={state.enabled}
              onChange={onGlobalEnabledToggle}
              disabled={isBusy}
            />
          </div>
          <div className='dedup-card__ttl'>
            <Form.Label htmlFor='dedup-global-ttl'>TTL</Form.Label>
            <InputGroup>
              <Form.Control
                id='dedup-global-ttl'
                type='number'
                min='0'
                value={state.ttl}
                onChange={onGlobalTtlChange}
                disabled={isBusy || !state.enabled}
              />
              <InputGroup.Text>sec</InputGroup.Text>
            </InputGroup>
            {formatTtl(state.ttl) && (
              <small className='dedup-card__ttl-human'>{formatTtl(state.ttl)}</small>
            )}
          </div>
        </div>
        <div className='dedup-section'>
          <div className='dedup-section__head'>
            <span className='dedup-section__title'>Services</span>
            <span className='dedup-section__count'>
              {enabledCount}/{DEDUP_CACHE_SERVICES.length} enabled
            </span>
          </div>
          <div className='dedup-grid'>
            {DEDUP_CACHE_SERVICES.map(({ key, label }) => {
              const service = state.services[key]
              return (
                <div
                  key={key}
                  className={`dedup-card ${service.enabled ? '' : 'dedup-card--off'}`}
                >
                  <div className='dedup-card__head'>
                    <span className='dedup-card__name'>{label}</span>
                    <Form.Check
                      type='switch'
                      id={`dedup-${key}-switch`}
                      checked={service.enabled}
                      onChange={() => onServiceEnabledToggle(key)}
                      disabled={isBusy}
                    />
                  </div>
                  <div className='dedup-card__ttl'>
                    <Form.Label htmlFor={`dedup-${key}-ttl`}>TTL</Form.Label>
                    <InputGroup>
                      <Form.Control
                        id={`dedup-${key}-ttl`}
                        type='number'
                        min='0'
                        value={service.ttl}
                        onChange={(e) => onServiceTtlChange(key, e)}
                        disabled={isBusy || !service.enabled}
                      />
                      <InputGroup.Text>sec</InputGroup.Text>
                    </InputGroup>
                    {formatTtl(service.ttl) && (
                      <small className='dedup-card__ttl-human'>{formatTtl(service.ttl)}</small>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <Form.Group className='dedup-desc'>
          <Form.Label htmlFor='dedup-cache__description'>
            Description
            <span className='dedup-required'>*</span>
          </Form.Label>
          <Form.Control
            as='textarea'
            rows={2}
            id='dedup-cache__description'
            placeholder='Reason for this change'
            value={state.desc}
            onChange={onDescChange}
            disabled={isBusy}
          />
        </Form.Group>
        <div className='dedup-actions'>
          <Button
            variant='primary'
            disabled={isBusy}
            onClick={handleSubmit}
          >
            {state.isSubmitting ? (
              <>
                <Spinner
                  animation='border'
                  size='sm'
                  className='me-2'
                />
                Updating…
              </>
            ) : (
              'Update'
            )}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default DeduplicationCache
